import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Lock, Eye, EyeOff, ArrowRight, BarChart3, Globe, Activity } from "lucide-react";
import Navbar from "../components/Navbar";
// import CustomCursor from "../components/CustomCursor";

const Admin = () => {
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const CORRECT_PASSWORD = "Meraki@2026#Pradip";

  useEffect(() => {
    // Check if already authenticated in this session
    const auth = sessionStorage.getItem("meraki_admin_auth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem("meraki_admin_auth", "true");
      setError("");
    } else {
      setError("Unauthorized access. Core credentials mismatch.");
      setPassword("");
    }
  };

  if (isAuthenticated) {
    return (
      <div className="h-screen w-screen bg-black overflow-hidden flex flex-col">
        {/* {/* <CustomCursor /> */} */}
        <div className="bg-[var(--ink)] border-b border-[var(--rule)] px-8 py-4 flex items-center justify-between z-[100]">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full border border-[var(--gold)] flex items-center justify-center text-[var(--gold)]">
              <BarChart3 size={20} />
            </div>
            <div>
              <span className="font-impact text-[10px] tracking-[0.4em] text-[var(--gold)] uppercase block">Executive Dashboard</span>
              <h1 className="text-white font-display text-xl">Meraki Analytics Studio</h1>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <a 
              href="/" 
              className="px-6 py-2 rounded-full border border-white/10 text-[10px] font-impact tracking-widest text-[var(--muted)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-all"
            >
              Back to Website
            </a>
            <div className="flex items-center gap-2 hidden md:flex">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[10px] font-impact tracking-widest text-[var(--muted)] uppercase">Live Stream Active</span>
            </div>
            <button 
              onClick={() => {
                sessionStorage.removeItem("meraki_admin_auth");
                setIsAuthenticated(false);
              }}
              className="px-6 py-2 rounded-full bg-[var(--gold)] text-[var(--ink)] text-[10px] font-impact tracking-widest uppercase hover:scale-105 transition-all"
            >
              Secure Logout
            </button>
          </div>
        </div>
        <div className="flex-1 w-full relative">
          <iframe 
            src="https://cloud.umami.is/share/GS10SomiExjnjwPx" 
            className="absolute inset-0 w-full h-full border-none"
            title="Meraki Analytics"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--ink)] text-[var(--cream)] relative overflow-hidden flex items-center justify-center px-6">
      {/* {/* <CustomCursor /> */} */}
      <div className="grain" />
      
      {/* Aesthetic Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[var(--gold)] opacity-[0.02] blur-[150px] rounded-full" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/grid-me.png')] opacity-[0.03]" />
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-3xl border border-[var(--gold)]/20 bg-white/5 flex items-center justify-center text-[var(--gold)] mx-auto mb-8 relative">
            <Lock size={32} />
            <div className="absolute inset-0 border border-[var(--gold)] rounded-3xl animate-ping opacity-20" />
          </div>
          <span className="font-impact text-[var(--gold)] tracking-[0.5em] uppercase text-xs mb-4 block">Restricted Area</span>
          <h1 className="font-display text-4xl md:text-5xl text-white mb-4">Command Center</h1>
          <p className="text-[var(--cream-muted)] text-sm font-light">Input administrative key to access real-time metrics.</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div className="relative group">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter Administrative Key"
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-white placeholder:text-white/20 focus:outline-none focus:border-[var(--gold)] transition-all pr-16 font-mono tracking-widest"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-6 top-1/2 -translate-y-1/2 text-white/40 hover:text-[var(--gold)] transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="text-red-500 text-[10px] font-impact tracking-widest uppercase text-center"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <button 
            type="submit"
            className="btn-primary w-full py-5 flex items-center justify-center gap-3 group"
          >
            Access Dashboard <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </form>

        <div className="mt-16 flex items-center justify-center gap-8 opacity-20">
          <div className="flex flex-col items-center gap-2">
            <Globe size={16} />
            <span className="text-[8px] font-impact tracking-widest uppercase">Global Monitor</span>
          </div>
          <div className="w-px h-8 bg-white/20" />
          <div className="flex flex-col items-center gap-2">
            <Activity size={16} />
            <span className="text-[8px] font-impact tracking-widest uppercase">Live Hydration</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Admin;
